/**
 * @flow weak
 */

import { createSelector, createStructuredSelector } from 'reselect';
import { List, Map } from 'immutable';
import moment from 'moment';
import { showEventDisclosure } from '../modal/getters';

const today = moment();
const ADMIN_ROLES = ['SuperAdmin', 'Admin', 'Developer', 'Sales', 'SalesAdmin'];

export const listing = (state) => {
  return state.listing;
};

export const session = (state) => {
  return state.session;
};

export const isFailed = createSelector(
    [listing],
    (listing) => {
      if (listing) {
        return listing.get('failed');
      }
    }
);

export const isFetching = createSelector(
    [listing],
    (listing) => {
      if (listing) {
        return listing.get('fetching');
      }
    }
);

export const isAuthorized = createSelector(
  [listing, session],
  (listing, session) => {
    if (listing && session.user) {
      const host = listing.getIn(['data', 'listing', 'host_id']) == session.user.id;
      const admin = ADMIN_ROLES.indexOf(session.user.role) !== -1;
      return admin || host;
    }
  }
);

export const membership = createSelector(
  [listing, session],
  (listing, session) => {
    if (listing && session.user) {
      const membershipId = listing.getIn(['data', 'listing', 'membership_id']);
      let membership = false;
      if (membershipId) {
        membership = listing.getIn(['data', 'memberships'])
        .find(membership => membership.get('id') == membershipId);
      }
      return membership;
    }
  }
);

export const canDesign = createSelector(
  [listing, session],
  (listing, session) => {
    if (listing && session.user) {
      const membershipId = listing.getIn(['data', 'listing', 'membership_id']);
      let membership = false;
      if (membershipId) {
        membership = listing.getIn(['data', 'memberships'])
        .find(membership => membership.get('id') == membershipId)
        .get('design');
      }
      const host = listing.getIn(['data', 'listing', 'host_id']) == session.user.id;
      const admin = ADMIN_ROLES.indexOf(session.user.role) !== -1;
      return admin || host || membership;
    }
  }
);

export const getListing = createSelector(
    [listing],
    (listing) => {
      if (listing) {
        return listing.get('data');
      }
      return null;
    }
);

export const getListingId = createSelector(
    [getListing],
    (listing) => {
      if (listing) {
        return listing.getIn(['listing', 'id']);
      }
      return null;
    }
);


export const getRates = createSelector(
    [getListing],
    (listing) => {
      if (listing) {
        return listing.get('rates');
      }
      return null;
    }
);

export const getCapacities = createSelector(
    [getListing],
    (listing) => {
      if (listing) {
        return listing.get('capacities');
      }
      return null;
    }
);

export const getCurrentListing = createSelector(
    [listing],
    (listing) => {
      if (listing) {
        return listing.getIn(['data'], null);
      }
      return null;
    }
);

export const title = createSelector(
    [listing],
    (listing) => {
      if (listing) {
        return listing.getIn(['data', 'listing', 'title'], null);
      }
    }
);

export const hostName = createSelector(
  [listing],
  (listing) => {
    if (listing && listing.get('data')) {
      const hostId = listing.getIn(['data', 'listing', 'host_id']);
      const host = listing.getIn(['data', 'users'])
      .find(user => user.get('id') == hostId);
      return host.get('last_name') ? host.get('first_name') + ' ' + host.get('last_name') : host.get('first_name');
    }
  }
);

export const coverPhoto = createSelector(
  [listing],
  (listing) => {
    if (listing && listing.get('data')) {
      const coverId = listing.getIn(['data', 'listing', 'cover_photo_id']);
      return listing.getIn(['data', 'images'])
      .find(image => image.get('id') == coverId);
    }
  }
);

export const location = createSelector(
  [listing],
  (listing) => {
    if (listing && listing.get('data')) {
      return listing.getIn(['data', 'listing', 'venue_name']) || listing.getIn(['data', 'listing', 'address']);
    }
  }
);

export const dateTime = createSelector(
    [listing],
    (listing) => {
      if (listing && listing.get('data')) {
        return listing.getIn(['data', 'events'])
        .map(event => {
          return {
            id: event.get('id'),
            end_stamp: event.get('end_stamp'),
            end_time: event.get('end_time'),
            start_stamp: event.get('start_stamp'),
            start_time: event.get('start_time'),
            tz: event.get('tz'),
            state: event.get('state'),
            has_sale_ended: event.get('has_sale_ended')
          };
        })
        .sortBy(value => value.start_stamp)
        .filter(dateObj => today.diff(dateObj.end_time) < 0);
      }
    }
);

export const dateSummary = createSelector(
  [dateTime],
  (dateTime) => {
    if (dateTime) {
      const date = dateTime.first();
      return date;
    }
  }
);

export const locationData = createSelector(
  [listing],
  (listing) => {
    if (listing && listing.get('data')) {
      return {
        lat: listing.getIn(['data', 'listing', 'latitude']),
        lng: listing.getIn(['data', 'listing', 'longitude']),
        address: listing.getIn(['data', 'listing', 'address']),
        venue: listing.getIn(['data', 'listing', 'venue_name']) ? listing.getIn(['data', 'listing', 'venue_name']) : listing.getIn(['data', 'listing', 'title']),
        showMarker: listing.getIn(['data', 'listing', 'show_marker'])
      };
    }
    return null;
  }
);

export const description = createSelector(
  [listing],
  (listing) => {
    if (listing && listing.get('data')) {
      return listing.getIn(['data', 'listing', 'description_html'], null);
    }
  }
);

export const hostInfo = createSelector(
  [listing],
  (listing) => {
    if (listing && listing.get('data')) {
      return {
        last_name: listing.getIn(['data', 'users']).first().get('last_name'),
        name: listing.getIn(['data', 'users']).first().get('first_name'),
        description: listing.getIn(['data', 'users']).first().get('description'),
        pic: listing.getIn(['data', 'users']).first().get('image_url_500'),
        id: listing.getIn(['data', 'users']).first().get('id'),
        location: listing.getIn(['data', 'users']).first().get('short_description'),
        slug: listing.getIn(['data', 'users']).first().get('slug')
      };
    }
    return null;
  }
);

export const getTags = createSelector(
  [getListing],
  (listing) => {
    if (listing && listing.get('tags')) {
      return listing.get('tags');
    }
    return null;
  }
);

export const getPrices = createSelector(
  [getRates, getCapacities],
  (rates, capacities) => {
    if (rates && capacities) {
      return rates
      .filter(rate => rate.get('client_group') !== 'physical_only')
      .filter(rate => !['PwywRate', 'AddOnRate'].includes(rate.get('_type')))
      .filter(rate => {
        const startTime = rate.get('start_time');
        const endTime = rate.get('end_time');

        if (!startTime && !endTime) {
          return true;
        }
        else if (startTime && !endTime) {
          return today.diff(startTime) >= 0;
        }
        else if (!startTime && endTime) {
          return today.diff(endTime) < 0;
        }

        return today.diff(startTime) >= 0 && today.diff(endTime) < 0;
      })
      .map(rate => {
        let capacity = true;
        if (rate.get('capacity')) {
          capacity = rate.get('capacity_ids')
            .map(id => capacities.find(cap => cap.get('id') === id))
            .map(item => item && item.get('count_available') > 0)
            .find(Boolean) || false;
        }

        let prices = [];
        if (rate.get('price_levels')) {
          prices = rate.get('price_levels').map(priceLevel => {
            return priceLevel.get('display_price') || priceLevel.get('price');
          });
        }
        else {
          prices = List([rate.get('display_price') || rate.get('price')]);
        }

        return prices.map(price => {
          return Map({
            price,
            capacity
          });
        });
      }).flatMap(price => price);
    }
    return null;
  }
);

export const getImages = createSelector(
  [getListing],
  (listing) => {
    if (listing) {
      const imageIds = listing.getIn(['listing', 'image_ids']);
      return listing.get('images').filter(image => imageIds.contains(image.get('id')));
    }
    return null;
  }
);

export const getSlugParam = createSelector(
  [getListing],
  (listing) => {
    if (listing) {
      return listing.getIn(['listing', 'slug_param']);
    }
    return null;
  }
);

export const getListingUsers = createSelector(
  [getListing],
  (listing) => {
    if (listing) {
      return listing.get('users');
    }
    return null;
  }
);

export const allowSocialShare = createSelector(
  [listing],
  (listing) => {
    if (listing && listing.get('data')) {
      return listing.getIn(['data', 'listing', 'show_social_buttons'], null);
    }
  }
);

export const hideDate = createSelector(
  [listing],
  (listing) => {
    if (listing && listing.get('data')) {
      return listing.getIn(['data', 'listing', 'hide_date'], null);
    }
  }
);

export const getGTM = createSelector(
  [getListing],
  (listing) => {
    if (listing) {
      return listing.getIn(['listing', 'google_tag_manager_ids']);
    }
    return null;
  }
);


// connector
export default createStructuredSelector({
  showEventDisclosure,
  listing,
  session,
  isFailed,
  isFetching,
  isAuthorized,
  membership,
  canDesign,
  getListing,
  getListingId,
  getRates,
  getCapacities,
  getCurrentListing,
  title,
  hostName,
  coverPhoto,
  location,
  dateTime,
  dateSummary,
  locationData,
  description,
  hostInfo,
  getTags,
  getPrices,
  getImages,
  getSlugParam,
  getListingUsers,
  allowSocialShare,
  hideDate,
  getGTM
});



// WEBPACK FOOTER //
// ./src/modules/listing/getters.js